import type {
  AddressOptionsQueryDTO,
  CustomerListQueryDTO,
  CustomerOrdersQueryDTO,
  CustomerProductAffinityQueryDTO,
  MergeCustomersDTO,
  UpdateCodRiskSettingsDTO,
  UpdateCustomerDTO,
} from "@/modules/customers/domain/contracts";
import type { CodRiskSettingsDTO } from "@/modules/customers/domain/cod-risk-settings";
import type {
  AddressOptionsResult,
  CustomerOrdersResult,
  CustomerProfileResult,
  CustomerProfileProductAffinityResultDTO,
  CustomerRiskDTO,
  CustomersListResult,
  CustomersSearchResult,
  MergeCustomersPreviewResult,
  MergeCustomersResultDTO,
} from "@/modules/customers/domain/types";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export const customersApi = {
  async list(query?: CustomerListQueryDTO): Promise<CustomersListResult> {
    const url = new URL(`${config.apiUrl}/customers`);
    if (query?.q) url.searchParams.set("q", query.q);
    if (query?.province) url.searchParams.set("province", query.province);
    if (query?.district) url.searchParams.set("district", query.district);
    if (query?.risk) url.searchParams.set("risk", query.risk);
    url.searchParams.set("limit", String(query?.limit ?? 20));
    url.searchParams.set("offset", String(query?.offset ?? 0));
    return fetcher.get(url.toString());
  },
  async getRiskSettings(): Promise<CodRiskSettingsDTO> {
    return fetcher.get(`${config.apiUrl}/customers/risk-settings`);
  },
  async updateRiskSettings(
    input: UpdateCodRiskSettingsDTO,
  ): Promise<CodRiskSettingsDTO> {
    return fetcher.patch(`${config.apiUrl}/customers/risk-settings`, input);
  },
  async search(q: string): Promise<CustomersSearchResult> {
    const url = new URL(`${config.apiUrl}/customers/search`);
    url.searchParams.set("q", q);
    return fetcher.get(url.toString());
  },
  async addressOptions(
    query: AddressOptionsQueryDTO,
  ): Promise<AddressOptionsResult> {
    const url = new URL(`${config.apiUrl}/customers/address-options`);
    url.searchParams.set("level", query.level);
    if (query.province) url.searchParams.set("province", query.province);
    if (query.district) url.searchParams.set("district", query.district);
    if (query.q) url.searchParams.set("q", query.q);
    return fetcher.get(url.toString());
  },
  async lookup(phone: string): Promise<CustomerProfileResult> {
    const url = new URL(`${config.apiUrl}/customers/lookup`);
    url.searchParams.set("phone", phone);
    return fetcher.get(url.toString());
  },
  async get(id: string): Promise<CustomerProfileResult> {
    return fetcher.get(`${config.apiUrl}/customers/${id}`);
  },
  async update(
    id: string,
    input: UpdateCustomerDTO,
  ): Promise<CustomerProfileResult> {
    return fetcher.patch(`${config.apiUrl}/customers/${id}`, input);
  },
  async listOrders(
    id: string,
    query?: CustomerOrdersQueryDTO,
  ): Promise<CustomerOrdersResult> {
    const url = new URL(`${config.apiUrl}/customers/${id}/orders`);
    url.searchParams.set("limit", String(query?.limit ?? 20));
    url.searchParams.set("offset", String(query?.offset ?? 0));
    return fetcher.get(url.toString());
  },
  async risk(
    id: string,
    query?: { proposedTotal?: number },
  ): Promise<CustomerRiskDTO> {
    const url = new URL(`${config.apiUrl}/customers/${id}/risk`);
    if (query?.proposedTotal != null) {
      url.searchParams.set("proposedTotal", String(query.proposedTotal));
    }
    return fetcher.get(url.toString());
  },
  async productAffinity(
    id: string,
    query?: CustomerProductAffinityQueryDTO,
  ): Promise<CustomerProfileProductAffinityResultDTO> {
    const url = new URL(`${config.apiUrl}/customers/${id}/product-affinity`);
    if (query?.limit != null) {
      url.searchParams.set("limit", String(query.limit));
    }
    return fetcher.get(url.toString());
  },
  async mergePreview(input: {
    sourceId: string;
    targetId: string;
  }): Promise<MergeCustomersPreviewResult> {
    const url = new URL(`${config.apiUrl}/customers/merge-preview`);
    url.searchParams.set("sourceId", input.sourceId);
    url.searchParams.set("targetId", input.targetId);
    return fetcher.get(url.toString());
  },
  async merge(input: MergeCustomersDTO): Promise<MergeCustomersResultDTO> {
    return fetcher.post(`${config.apiUrl}/customers/merge`, input);
  },
};
